/*
 **	Modern Floating Tab Bar Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { OCAColors, Typography, Animation } from '../../theme/colors'
import { MaterialIcon } from './MaterialIcon'

interface Tab {
  id: string
  label: string
  icon: string
  path: string
}

const tabs: Tab[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    path: '/dashboard'
  },
  {
    id: 'zones',
    label: 'Zones',
    icon: 'location_on',
    path: '/zones'
  },
  {
    id: 'devices',
    label: 'Devices',
    icon: 'devices',
    path: '/devices'
  },
  {
    id: 'alarms',
    label: 'Logs',
    icon: 'notifications',
    path: '/alarms'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    path: '/settings'
  }
]

export const TabBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [pressedTab, setPressedTab] = React.useState<string | null>(null)

  const isActive = (path: string) => location.pathname === path

  const handleTabPress = (tab: Tab) => {
    if (!isActive(tab.path)) {
      navigate(tab.path)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '16px',
      right: '16px',
      height: '72px',
      backgroundColor: 'rgba(28, 28, 30, 0.95)',
      borderRadius: '24px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '0 8px',
    }}>
      {tabs.map((tab) => {
        const active = isActive(tab.path)
        const pressed = pressedTab === tab.id

        return (
          <button
            key={tab.id}
            onClick={() => handleTabPress(tab)}
            onTouchStart={() => setPressedTab(tab.id)}
            onTouchEnd={() => setPressedTab(null)}
            onTouchCancel={() => setPressedTab(null)}
            onMouseDown={() => setPressedTab(tab.id)}
            onMouseUp={() => setPressedTab(null)}
            onMouseLeave={() => setPressedTab(null)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '10px 8px',
              border: 'none',
              background: 'transparent',
              borderRadius: '16px',
              cursor: 'pointer',
              transition: `all ${Animation.normal} cubic-bezier(0.4, 0, 0.2, 1)`,
              transform: pressed ? 'scale(0.92)' : 'scale(1)',
              opacity: pressed ? 0.6 : 1,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            {/* Icon */}
            <MaterialIcon
              name={tab.icon}
              size={24}
              color={active ? OCAColors.primary : 'rgba(255, 255, 255, 0.6)'}
              style={{
                transition: `all ${Animation.normal} cubic-bezier(0.4, 0, 0.2, 1)`,
                transform: active ? 'translateY(-2px)' : 'translateY(0)',
              }}
            />

            {/* Label */}
            <span style={{
              fontSize: active ? Typography.fontSize.xs : '10px',
              fontWeight: active ? Typography.fontWeight.semibold : Typography.fontWeight.medium,
              color: active ? OCAColors.primary : 'rgba(255, 255, 255, 0.6)',
              transition: `all ${Animation.normal} ${Animation.easeOut}`,
              letterSpacing: '0.2px',
            }}>
              {tab.label}
            </span>

            {/* Active indicator dot */}
            {active && (
              <div style={{
                position: 'absolute',
                bottom: '4px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: OCAColors.primary,
                boxShadow: `0 0 8px ${OCAColors.primary}`,
                animation: 'slideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              }} />
            )}
          </button>
        )
      })}

      {/* Animations */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-50%) scale(0);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
        }
      `}</style>
    </div>
  )
}
